import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import * as LucideIcons from 'lucide-react';

// --- GÜVENLİ BİLEŞENLER ---
const SafeIcon = ({ name, className }) => {
  const IconComponent = LucideIcons[name] || LucideIcons.Zap;
  return <IconComponent className={className} />;
};

const CustomAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);
  if (!items || !Array.isArray(items)) return null;

  return (
    <div className="space-y-4">
      {items.map((item, i) => (
        <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="w-full flex justify-between items-center p-5 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <span>{item.question}</span>
            <LucideIcons.ChevronDown className={`w-5 h-5 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
          </button>
          {openIndex === i && (
            <div className="p-5 pt-0 text-slate-600 border-t border-slate-100 animate-fade-in-down" dangerouslySetInnerHTML={{ __html: item.answer }}></div>
          )}
        </div>
      ))}
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { data, loading, error, refreshData } = useData();
  const [isVisible, setIsVisible] = useState(false);
  const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
  const [contactForm, setContactForm] = useState({ full_name: '', phone: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    setIsVisible(true);
    if (data?.sliders?.length > 1) {
      const timer = setInterval(() => {
        setCurrentSliderIndex((prev) => (prev + 1) % data.sliders.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-amber-500 border-opacity-50 mx-auto mb-6"></div>
          <p className="text-amber-500 font-medium tracking-widest animate-pulse">BAŞKENT ENERJİ YÜKLENİYOR</p>
        </div>
      </div>
    );
  }

  const { settings = {}, about = {}, sliders = [], services = [], projects = [], faqs = [] } = data || {};

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${settings?.whatsapp || '905393254847'}`, '_blank');
  };

  const navLinks = [
    { name: 'Ana Sayfa', href: '#anasayfa' },
    { name: 'Hakkımızda', href: '#hakkimizda' },
    { name: 'Hizmetler', href: '#hizmetler' },
    { name: 'Projelerimiz', href: '/projeler' },
    { name: 'Galeri', href: '#galeri' },
    { name: 'İletişim', href: '#iletisim' },
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.full_name || !contactForm.phone || !contactForm.message) {
      alert("Lütfen zorunlu alanları doldurun.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      const result = await response.json();
      if (result.success) {
        alert("Mesajınız başarıyla iletildi. En kısa sürede size dönüş yapacağız.");
        setContactForm({ full_name: '', phone: '', subject: '', message: '' });
        refreshData();
      } else {
        alert("Hata: " + result.error);
      }
    } catch (error) {
      alert("Bir hata oluştu. Lütfen daha sonra tekrar deneyin.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentSlider = sliders[currentSliderIndex] || {
    badge: 'Başkent Enerji ve Savunma',
    title: 'Geleceğin Enerjisini Bugün İnşa Ediyoruz',
    subtitle: 'Yüksek teknoloji, sürdürülebilir enerji ve savunma sistemlerinde güvenilir çözüm ortağınız.',
    image_url: 'https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg'
  };

  const workProcess = [
    { step: '01', title: 'Analiz & Keşif', desc: 'İhtiyaçlarınızı yerinde analiz ederek en verimli mühendislik çözümünü planlıyoruz.', icon: 'Search' },
    { step: '02', title: 'Projelendirme', desc: 'Uluslararası standartlara uygun, detaylı teknik çizimler ve projeler hazırlıyoruz.', icon: 'FileText' },
    { step: '03', title: 'Uygulama', desc: 'Uzman kadromuzla projeyi en yüksek güvenlik ve kalite standartlarında hayata geçiriyoruz.', icon: 'Settings' },
    { step: '04', title: 'Destek & Bakım', desc: 'Teslimat sonrası 7/24 teknik destek ve periyodik bakım hizmetleri sunuyoruz.', icon: 'ShieldCheck' }
  ];

  const whyUs = [
    { title: 'Uzman Kadro', desc: 'Alanında deneyimli mühendis ve teknik ekibimizle profesyonel çözümler sunuyoruz.', icon: 'Users' },
    { title: 'Yüksek Kalite', desc: 'Kullandığımız tüm materyaller ve işçiliğimiz uluslararası kalite standartlarındadır.', icon: 'Award' },
    { title: 'Zamanında Teslim', desc: 'Projelerimizi söz verdiğimiz süre içerisinde, eksiksiz bir şekilde teslim ediyoruz.', icon: 'Clock' },
    { title: '7/24 Destek', desc: 'Satış sonrası teknik destek ve bakım hizmetlerimizle her zaman yanınızdayız.', icon: 'Headphones' }
  ];

  // Schema.org Structured Data
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": settings.site_title || "Başkent Enerji",
    "image": settings.logo_url,
    "@id": "https://baskentelektrik.net",
    "url": "https://baskentelektrik.net",
    "telephone": settings.phone,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": settings.address,
      "addressLocality": "Ankara",
      "addressCountry": "TR"
    },
    "description": settings.site_description,
    "sameAs": [
      settings.facebook,
      settings.instagram,
      settings.twitter,
      settings.linkedin
    ]
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-amber-500 selection:text-white overflow-x-hidden">
      {/* SEO & Analytics */}
      {settings.google_search_console && (
        <div dangerouslySetInnerHTML={{ __html: settings.google_search_console }} />
      )}
      {settings.google_analytics && (
        <div dangerouslySetInnerHTML={{ __html: settings.google_analytics }} />
      )}

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <img
              src={settings?.white_logo_url || "https://baskentelektrik.net/baskentelektrik/assets/img/logo/white-logo.png"}
              alt="Başkent Enerji"
              className="h-12 md:h-14 object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          </div>
          <nav className="hidden lg:flex space-x-10 text-sm font-bold uppercase tracking-widest text-slate-300">
            {navLinks.map((link) => (
              <a key={link.name} href={link.href} className="hover:text-amber-500 transition-colors">{link.name}</a>
            ))}
          </nav>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleWhatsApp}
              className="hidden md:block bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-full font-bold text-sm transition-all transform hover:scale-105 shadow-lg shadow-amber-600/20"
            >
              TEKLİF ALIN
            </button>
            <button
              className="lg:hidden text-white p-2 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <LucideIcons.X className="w-7 h-7" /> : <LucideIcons.Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[70px] bg-slate-950 z-[9999] flex flex-col p-8 space-y-6 overflow-y-auto border-t border-slate-800 h-[calc(100vh-70px)]">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-xl font-bold text-white hover:text-amber-500 transition-colors border-b border-slate-900 pb-4 flex justify-between items-center group"
              >
                <span>{link.name}</span>
                <LucideIcons.ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-500 transition-colors" />
              </a>
            ))}
            <div className="pt-6 space-y-4">
              <button
                onClick={() => { handleWhatsApp(); setIsMobileMenuOpen(false); }}
                className="w-full bg-amber-600 text-white py-4 rounded-xl font-black tracking-widest text-center shadow-lg shadow-amber-600/20"
              >
                TEKLİF ALIN
              </button>
              <div className="flex justify-center space-x-6 pt-4">
                <a href={settings.facebook} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white"><LucideIcons.Facebook className="w-6 h-6" /></a>
                <a href={settings.instagram} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white"><LucideIcons.Instagram className="w-6 h-6" /></a>
                <a href={settings.twitter} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-white"><LucideIcons.Twitter className="w-6 h-6" /></a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="anasayfa" className="relative h-screen min-h-[700px] flex items-center overflow-hidden bg-slate-950">
        <div
          className="absolute inset-0 bg-cover bg-center transition-all duration-1000 scale-105"
          style={{ backgroundImage: `url(${currentSlider.image_url})`, opacity: 0.4 }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/60 to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className={`max-w-3xl transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <span className="inline-block px-4 py-1.5 bg-amber-600 text-white font-bold text-xs uppercase tracking-[0.2em] rounded-sm mb-6">
              {currentSlider.badge}
            </span>
            <h1 className="text-5xl md:text-7xl font-black text-white mb-8 leading-[1.1]">
              {currentSlider.title}
            </h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed max-w-xl">
              {currentSlider.subtitle}
            </p>
            <div className="flex flex-wrap gap-5">
              <button onClick={handleWhatsApp} className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-4 rounded-sm font-black text-sm tracking-widest transition-all">
                BİZE ULAŞIN
              </button>
              <button onClick={() => document.getElementById('hizmetler').scrollIntoView({ behavior: 'smooth' })} className="border border-white/30 hover:bg-white/10 text-white px-10 py-4 rounded-sm font-black text-sm tracking-widest transition-all">
                HİZMETLERİMİZ
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="hakkimizda" className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-20 items-center">
            <div className="lg:w-1/2 relative">
              <div className="relative z-10">
                <img
                  src={about.image1_url || "https://images.pexels.com/photos/257736/pexels-photo-257736.jpeg"}
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                  alt="Hakkımızda"
                />
              </div>
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-amber-600/10 -z-10 rounded-full blur-3xl"></div>
              <div className="absolute -top-10 -left-10 w-64 h-64 bg-blue-600/10 -z-10 rounded-full blur-3xl"></div>
            </div>
            <div className="lg:w-1/2">
              <span className="text-amber-600 font-black text-sm tracking-[0.3em] uppercase mb-4 block">{about.badge || 'KURUMSAL'}</span>
              <h2 className="text-4xl md:text-5xl font-black mb-8 leading-tight">{about.title}</h2>
              <div className="text-lg text-slate-600 leading-relaxed space-y-6" dangerouslySetInnerHTML={{ __html: about.content1 }}></div>
              <div className="text-lg text-slate-600 leading-relaxed mt-6" dangerouslySetInnerHTML={{ __html: about.content2 }}></div>
              <div className="mt-12 grid grid-cols-2 gap-8 border-t border-slate-100 pt-12">
                <div>
                  <h4 className="text-3xl font-black text-amber-600 mb-2">20+</h4>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Yıllık Tecrübe</p>
                </div>
                <div>
                  <h4 className="text-3xl font-black text-amber-600 mb-2">500+</h4>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Tamamlanan Proje</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-32 bg-slate-950 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-20">
            <span className="text-amber-500 font-black text-sm tracking-[0.3em] uppercase mb-4 block">NEDEN BİZ?</span>
            <h2 className="text-4xl md:text-5xl font-black">Bizi Farklı Kılan Özelliklerimiz</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {whyUs.map((item, i) => (
              <div key={i} className="p-10 bg-slate-900 rounded-3xl border border-slate-800 hover:border-amber-500 transition-all group">
                <div className="w-14 h-14 bg-amber-600/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-amber-600 transition-colors">
                  <SafeIcon name={item.icon} className="w-7 h-7 text-amber-500 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-black mb-4">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="hizmetler" className="py-32 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="text-amber-600 font-black text-sm tracking-[0.3em] uppercase mb-4 block">UZMANLIK ALANLARIMIZ</span>
            <h2 className="text-4xl md:text-5xl font-black mb-6">Profesyonel Mühendislik Çözümleri</h2>
            <p className="text-slate-500 text-lg">Enerjiden savunmaya, her alanda en yüksek kalite ve güven standartlarıyla hizmet veriyoruz.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {services.map((service, i) => (
              <div key={i} className="group bg-white p-10 rounded-3xl shadow-sm hover:shadow-2xl transition-all duration-500 border border-slate-100 hover:-translate-y-2">
                <div className="w-16 h-16 bg-slate-950 text-amber-500 rounded-2xl flex items-center justify-center mb-8 group-hover:bg-amber-600 group-hover:text-white transition-colors duration-500">
                  <SafeIcon name={service.icon_name} className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-black mb-4 group-hover:text-amber-600 transition-colors">{service.title}</h3>
                <div className="text-slate-500 leading-relaxed" dangerouslySetInnerHTML={{ __html: service.description }}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Process Section */}
      <section className="py-32 bg-slate-950 text-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-24">
            <span className="text-amber-500 font-black text-sm tracking-[0.3em] uppercase mb-4 block">SÜREÇ YÖNETİMİ</span>
            <h2 className="text-4xl md:text-5xl font-black">Nasıl Çalışıyoruz?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 relative">
            {/* Connecting Line */}
            <div className="hidden lg:block absolute top-12 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

            {workProcess.map((item, i) => (
              <div key={i} className="relative z-10 text-center group">
                <div className="w-24 h-24 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:border-amber-500 transition-all duration-500 shadow-2xl">
                  <span className="text-4xl font-black text-amber-500">{item.step}</span>
                </div>
                <h3 className="text-xl font-black mb-4">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects / Gallery Section */}
      <section id="projeler" className="py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
            <div className="max-w-2xl">
              <span className="text-amber-600 font-black text-sm tracking-[0.3em] uppercase mb-4 block">REFERANSLARIMIZ</span>
              <h2 className="text-4xl md:text-5xl font-black">Tamamlanan Projeler</h2>
            </div>
            <button onClick={() => navigate('/projeler')} className="text-amber-600 font-black text-sm tracking-widest border-b-2 border-amber-600 pb-2 hover:text-amber-700 hover:border-amber-700 transition-all">
              TÜMÜNÜ GÖRÜNTÜLE
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 6).map((project, i) => (
              <div key={i} className="group relative overflow-hidden rounded-3xl cursor-pointer bg-slate-50 border border-slate-200 p-8 hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="flex flex-col h-full justify-between">
                  <div>
                    <span className="bg-amber-100 text-amber-600 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest mb-4 inline-block">
                      {project.category}
                    </span>
                    <h3 className="text-xl font-black text-slate-900 group-hover:text-amber-600 transition-colors">{project.title}</h3>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="text-slate-500 text-sm line-clamp-3" dangerouslySetInnerHTML={{ __html: project.description || '' }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {data?.gallery?.length > 0 && (
        <section id="galeri" className="py-32 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-20">
              <span className="text-amber-600 font-black text-sm tracking-[0.3em] uppercase mb-4 block">GALERİ</span>
              <h2 className="text-4xl md:text-5xl font-black">Çalışmalarımızdan Kareler</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {data.gallery.map((item, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedImage(item.image_url)}
                  className="group relative aspect-square overflow-hidden rounded-2xl cursor-pointer shadow-md"
                >
                  <img src={item.image_url} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt={item.title} />
                  <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-4 text-center">
                    <div>
                      <p className="text-white font-bold text-sm mb-1">{item.title}</p>
                      <p className="text-amber-500 text-[10px] font-black uppercase tracking-widest">{item.category}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lightbox Modal */}
          {selectedImage && (
            <div
              className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in duration-300"
              onClick={() => setSelectedImage(null)}
            >
              <button
                className="absolute top-6 right-6 text-white hover:text-amber-500 transition-colors z-[110]"
                onClick={() => setSelectedImage(null)}
              >
                <LucideIcons.X className="w-10 h-10" />
              </button>
              <img
                src={selectedImage}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-300"
                alt="Enlarged"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-32 bg-slate-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-20">
            <span className="text-amber-600 font-black text-sm tracking-[0.3em] uppercase mb-4 block">S.S.S</span>
            <h2 className="text-4xl md:text-5xl font-black">Sıkça Sorulan Sorular</h2>
          </div>
          <CustomAccordion items={faqs} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="iletisim" className="py-32 bg-slate-950 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
            <div>
              <span className="text-amber-500 font-black text-sm tracking-[0.3em] uppercase mb-4 block">İLETİŞİM</span>
              <h2 className="text-5xl font-black mb-10">Bizimle İletişime Geçin</h2>
              <p className="text-slate-400 text-xl mb-12 leading-relaxed">
                Projeleriniz için profesyonel destek almak veya fiyat teklifi istemek için formu doldurabilir veya doğrudan bizi arayabilirsiniz.
              </p>
              <div className="space-y-8">
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <LucideIcons.Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Telefon</p>
                    <p className="text-xl font-black">{settings.phone}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <LucideIcons.Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">E-posta</p>
                    <p className="text-xl font-black">{settings.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-amber-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <LucideIcons.MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Adres</p>
                    <p className="text-lg font-bold">{settings.address}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-12 rounded-[40px] text-slate-900 shadow-2xl">
              <h3 className="text-3xl font-black mb-8">Hızlı Mesaj Gönder</h3>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Ad Soyad *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.full_name}
                      onChange={(e) => setContactForm({ ...contactForm, full_name: e.target.value })}
                      className="w-full p-4 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400">Telefon *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="w-full p-4 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400">Konu</label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="w-full p-4 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-amber-500 outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400">Mesajınız *</label>
                  <textarea
                    rows="4"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="w-full p-4 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-amber-500 outline-none resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-slate-950 text-white font-black py-5 rounded-xl hover:bg-amber-600 transition-all tracking-widest disabled:opacity-50"
                >
                  {isSubmitting ? 'GÖNDERİLİYOR...' : 'GÖNDER'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-500 py-20 border-t border-slate-900">
        <div className="container mx-auto px-4 text-center">
          <img
            src={settings?.white_logo_url || "https://baskentelektrik.net/baskentelektrik/assets/img/logo/white-logo.png"}
            alt="Logo"
            className="h-16 mx-auto mb-10 opacity-80"
          />
          <div className="flex justify-center space-x-8 mb-10 text-sm font-bold uppercase tracking-widest">
            <a href="#anasayfa" className="hover:text-white transition-colors">Ana Sayfa</a>
            <a href="#hakkimizda" className="hover:text-white transition-colors">Hakkımızda</a>
            <a href="#hizmetler" className="hover:text-white transition-colors">Hizmetler</a>
            <a href="/projeler" className="hover:text-white transition-colors">Projeler</a>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} {settings.site_title}. Tüm hakları saklıdır. Tasarım & Mühendislik: Başkent Enerji</p>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-8 right-8 z-50 bg-green-500 hover:bg-green-600 text-white p-5 rounded-full shadow-2xl transition-all hover:scale-110 group"
      >
        <LucideIcons.MessageCircle className="w-8 h-8" />
        <span className="absolute right-full mr-4 bg-slate-900 text-white px-4 py-2 rounded-lg text-xs font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
          WhatsApp Destek Hattı
        </span>
      </button>
    </div>
  );
};

export default Home;